// Prix publics : la SEULE source (cf. constants/plans.js). Le JSON-LD de
// /plans les lit d'ici pour ne jamais annoncer aux moteurs un prix que
// la page ne tient plus.
// Extension explicite : ce module est aussi importé par Node (scripts de
// build), qui ne complète pas les chemins comme webpack.
import { PRICE_EUR, PRICE_ANNUAL_EUR } from '../../constants/plans.js';

/**
 * Centralized SEO metadata system for Archer.
 * Used with react-helmet-async (see components/seo/PageHead.jsx).
 *
 * Two shapes coexist in PAGE_METADATA below:
 *   1. Flat entry (legacy US pillar pages) — a single generateMetadata()
 *      result, English-only. Kept as-is; those pages don't have a BE
 *      counterpart.
 *   2. Nested entry keyed by language ({fr, en, nl}) — used by the
 *      main public surfaces (home, plans, attorneys) which serve
 *      Belgium's tri-lingual audience. `getPageMetadata(key, lang)`
 *      picks the right one at runtime.
 */

// ⚠️ SITE_URL — set this to the production host at launch.
// Currently pointing at the final prod domain (archer.law); if we ship
// on a preview host first, keep this pointing at prod so canonicals /
// og:url continue to resolve to the launch domain (Search Console
// consolidates signals against the canonical, not the URL Google
// crawls). Never point this at a Vercel/Emergent preview.
const SITE_URL = 'https://archer.law';
const SITE_NAME = 'Archer';
// Carte de partage par défaut. RASTER, PAS SVG : Facebook, LinkedIn et
// WhatsApp refusent purement et simplement un og:image en SVG — le lien
// s'affiche alors sans vignette. Le fichier est produit par
// `frontend/scripts/build-og-image.mjs` et commité ; il est servi à la
// racine du build, donc accessible sans JS ni bundle.
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.png`;

// Which languages a multilingual page ships. Order = fallback priority
// (FR is our primary market, NL is the second Belgian side, EN is the
// international fallback + x-default target).
const SUPPORTED_LANGS = ['fr', 'nl', 'en'];
const LOCALE_MAP  = { fr: 'fr_BE', nl: 'nl_BE', en: 'en_US' };
const HREFLANG_MAP = { fr: 'fr-BE', nl: 'nl-BE', en: 'en' };


/**
 * Build a page metadata object.
 *
 * @param {Object} data
 * @param {string} data.title       50-60 chars max (site name appended)
 * @param {string} data.description 140-160 chars max
 * @param {string} [data.canonical] absolute URL — SITE_URL if omitted
 * @param {string} [data.ogTitle]   titre social, SANS « | Archer ». Sert
 *   quand la carte de partage doit accrocher autrement que l'onglet : le
 *   <title> travaille pour un résultat Google (mots-clés, marque en
 *   suffixe), la carte pour un fil LinkedIn (une promesse courte). Par
 *   défaut, la carte reprend le <title>, suffixe compris.
 * @param {string} [data.ogDescription] idem pour la description sociale.
 * @param {string} [data.ogImage]
 * @param {'website'|'article'|'product'} [data.ogType]
 * @param {'fr_BE'|'nl_BE'|'en_US'} [data.locale]
 * @param {boolean} [data.noindex]
 * @param {Object[]|Object} [data.jsonLd]      one or many JSON-LD documents
 * @param {Object.<string,string>} [data.alternates] {lang: absoluteUrl}
 * @param {Object} [data.article]              blog article extras
 */
export function generateMetadata(data) {
  // La marque se met en suffixe UNE fois. Un titre qui la porte déjà
  // (« Archer | Le droit de te défendre. », « Tarifs Archer », « Archer for
  // Teams ») ne la reçoit pas une seconde fois : « … | Archer | Archer »
  // faisait 85 signes sur l'accueil, coupés à 60 par Google.
  const title = /\bArcher\b/.test(data.title)
    ? data.title
    : `${data.title} | ${SITE_NAME}`;
  const canonical = data.canonical || SITE_URL;
  const ogImage = data.ogImage || DEFAULT_OG_IMAGE;
  const locale = data.locale || 'fr_BE';

  return {
    title,
    description: data.description,
    canonical,
    ogImage,
    ogTitle: data.ogTitle || null,
    ogDescription: data.ogDescription || null,
    locale,
    noindex: data.noindex || false,
    ogType: data.ogType || 'website',
    article: data.article || null,
    jsonLd: data.jsonLd || null,
    alternates: data.alternates || null,
  };
}


// ────────────────────────────────────────────────────────────────────
// Reusable JSON-LD blocks — shared across pages so structured data
// stays coherent (one Organization, one WebSite definition).
// ────────────────────────────────────────────────────────────────────
// Profils sociaux officiels d'Archer, pour `sameAs`. VIDE tant qu'aucune
// URL n'est confirmée : un `sameAs` qui pointe sur la mauvaise page
// LinkedIn (il existe plusieurs « Archer » juridiques) rattacherait la
// marque à un autre cabinet. À renseigner avec l'URL exacte de la page
// entreprise, puis relancer `npm run build`.
export const SOCIAL_PROFILES = [];

// UNE seule Organization pour tout le site. Le logo est un RASTER carré
// (180 × 180, `apple-touch-icon.png`) : Google exige au moins 112 × 112 et
// une image explorable ; le wordmark SVG de 294 octets en `<text>` ne
// passait pas. La description est en français, langue du HTML brut.
export const ORG_JSONLD = {
  '@context': 'https://schema.org',
  '@type':    'Organization',
  name:       SITE_NAME,
  url:        SITE_URL,
  logo:       `${SITE_URL}/apple-touch-icon.png`,
  description:
    'Archer analyse ton dossier juridique en quelques minutes, établit ton droit et fait agir un juriste : lettre, négociation, relance. Fait pour la Belgique.',
  areaServed: { '@type': 'Country', name: 'Belgium' },
  ...(SOCIAL_PROFILES.length ? { sameAs: SOCIAL_PROFILES } : {}),
};

export const WEBSITE_JSONLD = {
  '@context': 'https://schema.org',
  '@type':    'WebSite',
  name:       SITE_NAME,
  url:        SITE_URL,
  inLanguage: ['fr-BE', 'nl-BE', 'en'],
  // SearchAction intentionally omitted — the app has no public
  // sitewide search UI for Google to bind against.
};

// L'application elle-même. Le prix d'entrée payant est celui du Starter
// en cycle annuel, lu de constants/plans.js : jamais un chiffre recopié.
export const SOFTWARE_APP_JSONLD = {
  '@context': 'https://schema.org',
  '@type':    'SoftwareApplication',
  name:       SITE_NAME,
  url:        SITE_URL,
  applicationCategory: 'LegalTech',
  operatingSystem: 'Web',
  offers: {
    '@type':       'Offer',
    price:         '0',
    priceCurrency: 'EUR',
    description:   'Deux analyses complètes gratuites par mois',
  },
};

/**
 * Product JSON-LD d'une formule payante sur /plans.
 *
 * Deux offres par formule, parce que la page en vend deux : le mensuel
 * sans engagement, et l'annuel débité en une fois. Chaque offre porte sa
 * `UnitPriceSpecification` (durée de facturation en mois) : sans elle, un
 * « 34 € » nu annonçait aux moteurs un prix mensuel qu'aucun client ne
 * paie au mois. Les montants viennent de constants/plans.js.
 */
export function planOffer(tier, name, description) {
  const monthly = PRICE_EUR[tier];
  const annual = PRICE_ANNUAL_EUR[tier];
  const offer = (price, months) => ({
    '@type':        'Offer',
    priceCurrency:  'EUR',
    price:          String(price),
    availability:   'https://schema.org/InStock',
    url:            `${SITE_URL}/plans`,
    priceSpecification: {
      '@type':          'UnitPriceSpecification',
      price:            String(price),
      priceCurrency:    'EUR',
      billingDuration:  months,
      billingIncrement: 1,
      unitCode:         'MON',
    },
  });
  return {
    '@context': 'https://schema.org',
    '@type':    'Product',
    name,
    description,
    image:      DEFAULT_OG_IMAGE,
    brand:      { '@type': 'Brand', name: SITE_NAME },
    offers:     [offer(monthly, 1), offer(annual, 12)],
  };
}

const PLANS_JSONLD = [
  planOffer('starter', 'Archer Starter',
    'Un juriste Archer se bat pour toi : lettre, négociation, relance. Mensuel sans engagement, ou annuel débité en une fois.'),
  planOffer('pro', 'Archer Pro',
    'Deux dossiers juriste en parallèle, un appel, toute la famille. Mensuel sans engagement, ou annuel débité en une fois.'),
];


// ────────────────────────────────────────────────────────────────────
// Alternates helper. DÉCISION DU 02/09/2026 : fr-BE + x-default SEULEMENT.
//
// Les trois langues vivent à la même URL et le HTML brut est toujours
// français : un moteur n'indexe qu'un document par URL, donc il n'y a
// PAS de version NL ni EN à déclarer. Trois hreflang qui pointaient tous
// sur la même adresse ne disaient rien de vrai, et le sitemap (fr-BE
// seul sur les piliers) les contredisait. Les URL par langue (/nl/, /en/)
// sont un chantier de routage, reporté après le lancement presse.
// ────────────────────────────────────────────────────────────────────
function frOnlyAlternates(url) {
  return { fr: url };
}
const sameUrlAlternates = frOnlyAlternates;

// Redirections permanentes servies par le backend (301) ET par le routeur
// (Navigate) : une seule table. Quatre grappes se disputaient la même
// requête (huissier, caution, facture, PV) ; on garde le pilier, le plus
// complet, et les landings de campagne y renvoient. `/pricing` était une
// redirection cliente seulement, donc un 200 nu pour un robot.
export const REDIRECTS = {
  '/pv':       '/contester-amende-radar',
  '/vitesse':  '/contester-amende-radar',
  '/huissier': '/lettre-huissier-que-faire',
  '/caution':  '/caution-locative-non-rendue',
  '/facture':  '/facture-abusive-contester',
  '/pricing':  '/plans',
};


// ────────────────────────────────────────────────────────────────────
// PAGE_METADATA — nested {pageKey: {lang: metadata}} for multilingual
// surfaces; flat metadata for legacy US-only pillar pages.
// ────────────────────────────────────────────────────────────────────
export const PAGE_METADATA = {
  // ── Home ─────────────────────────────────────────────────────────
  // Titre d'accueil ≤ 60 signes, tagline figée comprise (cf.
  // check-no-em-dash : « Archer | Le droit de te défendre. » ne bouge pas).
  home: {
    fr: generateMetadata({
      title: 'Archer | Le droit de te défendre. | Aide juridique en ligne',
      description:
        'Deux analyses complètes gratuites par mois, score juridique, lettre envoyée : Archer transforme ton dossier en action concrète. Fait pour la Belgique.',
      canonical: SITE_URL,
      locale: 'fr_BE',
      alternates: sameUrlAlternates(SITE_URL),
      jsonLd: [ORG_JSONLD, WEBSITE_JSONLD, SOFTWARE_APP_JSONLD],
    }),
    en: generateMetadata({
      title: 'Archer | The right to defend yourself. | Online legal help',
      description:
        'Two free full analyses a month, legal score, letter sent: Archer turns your case into concrete action. Made for Belgium.',
      canonical: SITE_URL,
      locale: 'en_US',
      alternates: sameUrlAlternates(SITE_URL),
      jsonLd: [ORG_JSONLD, WEBSITE_JSONLD, SOFTWARE_APP_JSONLD],
    }),
    nl: generateMetadata({
      title: 'Archer | Het recht om jezelf te verdedigen. | Juridische hulp',
      description:
        'Twee gratis volledige analyses per maand, juridische score, verzonden brief: Archer maakt van jouw dossier concrete actie. Gemaakt voor België.',
      canonical: SITE_URL,
      locale: 'nl_BE',
      alternates: sameUrlAlternates(SITE_URL),
      jsonLd: [ORG_JSONLD, WEBSITE_JSONLD, SOFTWARE_APP_JSONLD],
    }),
  },

  // ── /plans ───────────────────────────────────────────────────────
  plans: {
    fr: generateMetadata({
      title: 'Tarifs Archer | Free, Starter, Pro',
      description:
        'Free : 2 analyses complètes par mois. Starter dès 34 €/mois : un juriste se bat pour toi. Pro dès 69 €/mois : 2 dossiers, appel, toute la famille.',
      canonical: `${SITE_URL}/plans`,
      locale: 'fr_BE',
      alternates: sameUrlAlternates(`${SITE_URL}/plans`),
      jsonLd: PLANS_JSONLD,
    }),
    en: generateMetadata({
      title: 'Archer Pricing | Free, Starter, Pro',
      description:
        'Free: 2 full analyses a month. Starter from €34/mo: a jurist fights for you. Pro from €69/mo: 2 cases, a call, your whole family. Switch anytime.',
      canonical: `${SITE_URL}/plans`,
      locale: 'en_US',
      alternates: sameUrlAlternates(`${SITE_URL}/plans`),
      jsonLd: PLANS_JSONLD,
    }),
    nl: generateMetadata({
      title: 'Archer Tarieven | Free, Starter, Pro',
      description:
        'Free: 2 volledige analyses per maand. Starter vanaf 34 €/maand: een jurist vecht voor jou. Pro vanaf 69 €/maand: 2 dossiers, een gesprek, het hele gezin.',
      canonical: `${SITE_URL}/plans`,
      locale: 'nl_BE',
      alternates: sameUrlAlternates(`${SITE_URL}/plans`),
      jsonLd: PLANS_JSONLD,
    }),
  },

  // ── /attorneys (« Qui se bat pour toi ») ─────────────────────────
  // La page ne vend plus une mise en relation avec un avocat : elle
  // présente les TROIS maillons de la chaîne — l'IA, le juriste salarié,
  // l'avocat partenaire — et le réseau qui les porte. Les titres et
  // descriptions suivent ce contenu ; l'ancienne promesse « sur le tarif
  // que tu choisis » disparaît avec eux, l'avocat proposant désormais
  // ses honoraires en direct.
  //
  // La structure hreflang ne bouge pas : trois langues à la même URL,
  // servies par `sameUrlAlternates`, comme l'accueil et /plans.
  attorneys: {
    fr: generateMetadata({
      title: 'Qui se bat pour toi | l’équipe juridique Archer',
      description:
        'Une IA qui analyse, un juriste salarié Archer qui agit, un avocat inscrit au barreau si ça va au tribunal. Archer t’oriente selon ton dossier.',
      canonical: `${SITE_URL}/attorneys`,
      locale: 'fr_BE',
      alternates: sameUrlAlternates(`${SITE_URL}/attorneys`),
    }),
    en: generateMetadata({
      title: 'Who fights for you | the Archer legal team',
      description:
        'An AI that analyses, a salaried Archer legal expert who acts, a bar-registered lawyer if it goes to court. Archer routes you according to your case.',
      canonical: `${SITE_URL}/attorneys`,
      locale: 'en_US',
      alternates: sameUrlAlternates(`${SITE_URL}/attorneys`),
    }),
    nl: generateMetadata({
      title: 'Wie voor jou vecht | het juridische team van Archer',
      description:
        'Een AI die analyseert, een Archer-jurist in dienst die handelt, een advocaat aan de balie als het naar de rechtbank gaat. Archer stuurt je volgens je dossier.',
      canonical: `${SITE_URL}/attorneys`,
      locale: 'nl_BE',
      alternates: sameUrlAlternates(`${SITE_URL}/attorneys`),
    }),
  },

  // ── /engine (« Notre IA ») ───────────────────────────────────────
  // La plongée technique. Elle était la SEULE page du menu à construire
  // ses balises à la volée, sans passer par cette table : elle avait donc
  // un titre, mais aucun `alternates` — pas de hreflang, alors qu'elle
  // sert ses trois langues à la même URL comme l'accueil et /plans.
  //
  // Les titres d'origine venaient de `i18n/engine.json` (« Sous le capot
  // — Archer »), ce qui donnait « Sous le capot — Archer | Archer » une
  // fois le nom du site ajouté par `generateMetadata`, sur une
  // description de 43 signes. Ils vivent ici désormais, à la bonne
  // longueur et sans marque en double ; les clés `meta.*` du
  // dictionnaire ont été retirées pour qu'il n'en reste qu'une source.
  //
  // AUCUN CHIFFRE DE VOLUMÉTRIE ici, volontairement. La taille du corpus
  // est publiée à UN seul endroit (`CORPUS_SOURCES` dans
  // `CinematiqueV6/cineData.js`), et `backend/tests/test_corpus_count.py`
  // la confronte au compte réel de la base. Une description figée qui
  // annoncerait « 300 000+ documents » serait un troisième chiffre publié,
  // sans garde-fou, qui se périmerait en silence au prochain lot embarqué.
  // Les SEPT PASSES, elles, sont une propriété d'architecture : elles ne
  // bougent pas avec les données.
  engine: {
    fr: generateMetadata({
      title: 'Notre IA | sous le capot du moteur juridique',
      description:
        'Sept passes de raisonnement, le droit belge fédéral et régional, la recherche augmentée et les garde-fous anti-invention : l’architecture d’Archer en entier.',
      canonical: `${SITE_URL}/engine`,
      locale: 'fr_BE',
      alternates: sameUrlAlternates(`${SITE_URL}/engine`),
    }),
    en: generateMetadata({
      title: 'Our AI | under the hood of the legal engine',
      description:
        'Seven reasoning passes, Belgian federal and regional law, retrieval-augmented search and anti-fabrication guardrails: Archer’s full architecture.',
      canonical: `${SITE_URL}/engine`,
      locale: 'en_US',
      alternates: sameUrlAlternates(`${SITE_URL}/engine`),
    }),
    nl: generateMetadata({
      title: 'Onze AI | onder de motorkap van de juridische motor',
      description:
        'Zeven redeneerpassages, Belgisch federaal en regionaal recht, verrijkte zoekopdrachten en waarborgen tegen verzinsels: de volledige architectuur van Archer.',
      canonical: `${SITE_URL}/engine`,
      locale: 'nl_BE',
      alternates: sameUrlAlternates(`${SITE_URL}/engine`),
    }),
  },

  // ── /entreprises (Archer for Teams) ──────────────────────────────
  // Page B2B : elle s'adresse aux décideurs RH belges et vouvoie. Le
  // CORPS de la page est traduit (i18n/teams.json porte fr, nl et en),
  // mais le title et la description restent FR faute de traduction
  // validée — `getPageMetadata` retombe sur `fr` pour les autres
  // langues. Les alternates pointent tous sur la même URL, qui est bien
  // la même page dans les trois langues.
  //
  // La CARTE de partage se dissocie ici du <title>. Le titre d'onglet
  // décrit l'offre pour Google (« le bureau juridique personnel de vos
  // équipes », 72 signes, suffixe de marque compris) ; la carte reprend
  // l'accroche du hero de la page — `hero.titleAccent` dans
  // i18n/teams.json — parce qu'un lien posé dans un fil LinkedIn RH est lu
  // en deux secondes et gagne à porter la promesse, pas la description.
  // C'est la même phrase que celle qui accueille le visiteur à l'arrivée :
  // la carte ne promet rien que la page ne tienne.
  teams: {
    fr: generateMetadata({
      title: 'Archer for Teams | Le bureau juridique personnel de vos équipes',
      description:
        'Après la santé, le juridique. Le bureau juridique personnel de chacun de vos collaborateurs, offert par vous. Dès 10 collaborateurs, pilote de 3 mois possible.',
      ogTitle: 'Archer for Teams | L’avantage qui manquait',
      canonical: `${SITE_URL}/entreprises`,
      locale: 'fr_BE',
      alternates: sameUrlAlternates(`${SITE_URL}/entreprises`),
    }),
  },

  // ── Les 16 landings thématiques (/pv, /telecom, …) ───────────────
  // Une porte d'entrée Google par thème : chacune a son title, sa
  // description et son canonical propres. FR-BE uniquement — ce sont
  // des pages de campagne, pas des pages de catalogue, et on ne
  // déclare pas d'alternate NL/EN qui pointerait sur du français.
  // La clé suit `landing-<slug>`, lue par pages/LandingThemePage.jsx.
  // ⚠️ TROIS PAIRES DE LANDINGS PARTAGENT LEUR CORPS.
  // pv/vitesse, arnaque/jamais-livre et assurance/sinistre-refuse rendent
  // les MÊMES arguments, les mêmes chiffres, et ne diffèrent que par le
  // titre de leur FAQ. Leurs descriptions se recopiaient donc elles aussi,
  // et les deux pages d'une même paire se disputaient la même requête.
  //
  // Elles sont réécrites pour se séparer sur l'INTENTION plutôt que sur le
  // contenu, seul angle disponible tant que les corps ne divergent pas :
  // /pv prend la contestation générique, /vitesse l'excès de vitesse ;
  // /arnaque le faux site et le vendeur fantôme, /jamais-livre la commande
  // non reçue sur une marketplace ; /assurance le refus d'indemniser en
  // général, /sinistre-refuse le refus expédié en lettre type.
  //
  // Elles ne partagent plus non plus leur phrase de fin : « …verdict en
  // quelques minutes. » fermait quatorze des seize descriptions. NEUF
  // landings l'ont encore (huissier, caution, licenciement, facture, bail,
  // telecom, energie, telephone-volant, abonnement-piege) — chantier
  // distinct, et sans urgence tant qu'elles ne se disputent pas une requête.
  //
  // Reste un recouvrement de vocabulaire entre /assurance et
  // /sinistre-refuse (refus, clause, expertise) : irréductible pour deux
  // pages qui traitent le refus d'un assureur, et redescendu au niveau des
  // paires légitimes du lot (/facture ~ /telecom, /licenciement ~ /bail).
  // /pv, /vitesse, /huissier, /caution et /facture ont QUITTÉ cette table le
  // 02/09/2026 : ils renvoient en 301 vers le pilier de leur grappe
  // (cf. REDIRECTS). Leur corps reste dans content/landings.js.
  'landing-licenciement': generateMetadata({
    title: 'Licenciement en Belgique | vérifie ce qu’on te doit',
    description:
      'Tu viens d’être licencié ? Préavis, indemnité, motivation, C4 : vérifie ce qu’on te doit avant de signer quoi que ce soit. Analyse gratuite en quelques minutes.',
    canonical: `${SITE_URL}/licenciement`,
    locale: 'fr_BE',
    alternates: { fr: `${SITE_URL}/licenciement` },
  }),
  'landing-arnaque': generateMetadata({
    title: 'Arnaque en ligne | jamais livré, jamais remboursé',
    description:
      'Faux site, vendeur fantôme, paiement envolé : une arnaque en ligne se combat. Fais établir ton droit au remboursement, gratuitement, en quelques minutes.',
    canonical: `${SITE_URL}/arnaque`,
    locale: 'fr_BE',
    alternates: { fr: `${SITE_URL}/arnaque` },
  }),
  'landing-bail': generateMetadata({
    title: 'Ton propriétaire te met dehors ? Vérifie ses droits',
    description:
      'Congé, préavis, indemnité, état des lieux : vérifie si ton propriétaire a le droit de te mettre dehors. Analyse gratuite du bail, verdict en quelques minutes.',
    canonical: `${SITE_URL}/bail`,
    locale: 'fr_BE',
    alternates: { fr: `${SITE_URL}/bail` },
  }),
  'landing-assurance': generateMetadata({
    title: 'Ton assureur refuse de payer | vérifie le refus',
    description:
      'Ton assureur refuse d’indemniser ? Clause d’exclusion, défaut d’entretien invoqué sans preuve, expertise à charge : fais peser le refus avant de l’accepter.',
    canonical: `${SITE_URL}/assurance`,
    locale: 'fr_BE',
    alternates: { fr: `${SITE_URL}/assurance` },
  }),
  'landing-telecom': generateMetadata({
    title: 'Facture télécom gonflée | Proximus, Orange, Telenet',
    description:
      'Frais de résiliation après l’engagement, options jamais souscrites, hausse unilatérale : fais vérifier ta facture télécom. Analyse gratuite en quelques minutes.',
    canonical: `${SITE_URL}/telecom`,
    locale: 'fr_BE',
    alternates: { fr: `${SITE_URL}/telecom` },
  }),
  'landing-energie': generateMetadata({
    title: 'Régularisation énergie | fais vérifier la facture',
    description:
      'Une régularisation à quatre chiffres ? Index estimés, tarif appliqué, prescription : fais vérifier la facture avant de payer. Analyse gratuite.',
    canonical: `${SITE_URL}/energie`,
    locale: 'fr_BE',
    alternates: { fr: `${SITE_URL}/energie` },
  }),
  'landing-sepa': generateMetadata({
    title: 'Prélèvement SEPA non autorisé | remboursement',
    description:
      'Un prélèvement que tu ne reconnais pas ? Tu disposes d’un droit au remboursement encadré par la loi. Fais vérifier ton relevé gratuitement.',
    canonical: `${SITE_URL}/sepa`,
    locale: 'fr_BE',
    alternates: { fr: `${SITE_URL}/sepa` },
  }),
  'landing-telephone-volant': generateMetadata({
    title: 'Téléphone au volant | que prouve vraiment le PV ?',
    description:
      'Verbalisé pour ton téléphone ? Constatation, mentions du procès-verbal, preuve de l’usage : fais vérifier le PV. Analyse gratuite, verdict en quelques minutes.',
    canonical: `${SITE_URL}/telephone-volant`,
    locale: 'fr_BE',
    alternates: { fr: `${SITE_URL}/telephone-volant` },
  }),
  'landing-jamais-livre': generateMetadata({
    title: 'Commande jamais livrée | Vinted, faux site, vendeur',
    description:
      'Vinted, marketplace, boutique en ligne : ta commande n’arrive pas et le vendeur se tait. Le remboursement intégral se réclame. Vérifie ce que tu peux exiger.',
    canonical: `${SITE_URL}/jamais-livre`,
    locale: 'fr_BE',
    alternates: { fr: `${SITE_URL}/jamais-livre` },
  }),
  'landing-abonnement-piege': generateMetadata({
    title: 'Abonnement piège | un essai gratuit qui te facture',
    description:
      'Un essai gratuit devenu payant sans ton accord ? Reconduction cachée, consentement absent : fais vérifier ton dossier. Analyse gratuite en quelques minutes.',
    canonical: `${SITE_URL}/abonnement-piege`,
    locale: 'fr_BE',
    alternates: { fr: `${SITE_URL}/abonnement-piege` },
  }),
  'landing-sinistre-refuse': generateMetadata({
    title: 'Sinistre refusé par lettre type | fais-le vérifier',
    description:
      'Un sinistre refusé en trois lignes n’est pas un refus motivé. Fais examiner l’expertise produite et la clause invoquée, puis conteste sur pièces.',
    canonical: `${SITE_URL}/sinistre-refuse`,
    locale: 'fr_BE',
    alternates: { fr: `${SITE_URL}/sinistre-refuse` },
  }),

  // ── Belgian pillar pages (FR-only for now — NL/EN scheduled) ────
  // The 4 first landing pages of BP §14.2. Each is FR-BE and shares
  // its slug across languages (SPA — no /fr/ prefix). Hreflang points
  // every supported language at the same URL; when NL/EN translations
  // ship, replace with proper `alternates` maps.
  'contester-amende-radar': generateMetadata({
    title: 'Contester une amende radar en Belgique | le guide',
    description:
      'PV de vitesse en Belgique : que vérifier avant de payer (signalisation, marge technique, homologation) et quand contester vs payer.',
    canonical: `${SITE_URL}/contester-amende-radar`,
    locale: 'fr_BE',
    alternates: sameUrlAlternates(`${SITE_URL}/contester-amende-radar`),
  }),
  'lettre-huissier-que-faire': generateMetadata({
    title: 'Lettre d’huissier : que faire | guide belge',
    description:
      'Courrier ou visite d’huissier en Belgique : mise en demeure, signification, saisie : comprends ce que l’huissier peut, et ce que tu peux faire.',
    canonical: `${SITE_URL}/lettre-huissier-que-faire`,
    locale: 'fr_BE',
    alternates: sameUrlAlternates(`${SITE_URL}/lettre-huissier-que-faire`),
  }),
  'caution-locative-non-rendue': generateMetadata({
    title: 'Caution locative non rendue | récupérer ta garantie',
    description:
      'Le propriétaire garde ta garantie locative ? Étapes concrètes pour la libérer : état des lieux, procédure amiable, juge de paix.',
    canonical: `${SITE_URL}/caution-locative-non-rendue`,
    locale: 'fr_BE',
    alternates: sameUrlAlternates(`${SITE_URL}/caution-locative-non-rendue`),
  }),
  'facture-abusive-contester': generateMetadata({
    title: 'Contester une facture abusive | le guide',
    description:
      'Facture gonflée ou prestations non demandées : comment contester par écrit, charge de la preuve et mise en demeure inverse.',
    canonical: `${SITE_URL}/facture-abusive-contester`,
    locale: 'fr_BE',
    alternates: sameUrlAlternates(`${SITE_URL}/facture-abusive-contester`),
  }),

  // ── /how-it-works (« Comment ça marche ») ────────────────────────
  // Jusqu'au 02/09/2026 : entrée plate, en anglais, qui promettait « in 60
  // Seconds » (faux en délai : l'analyse prend quelques minutes) et une
  // « attorney-signed response letter » (faux en produit : c'est un juriste
  // Archer qui rédige et signe, jamais un avocat). Trilingue comme /engine.
  howItWorks: {
    fr: generateMetadata({
      title: 'Comment ça marche | scanne, comprends, agis',
      description:
        'Tu scannes ton document, Archer l’analyse en quelques minutes, établit ton droit avec ses sources, et un juriste rédige la lettre. Le parcours, étape par étape.',
      canonical: `${SITE_URL}/how-it-works`,
      locale: 'fr_BE',
      alternates: frOnlyAlternates(`${SITE_URL}/how-it-works`),
    }),
    en: generateMetadata({
      title: 'How it works | scan, understand, act',
      description:
        'You scan your document, Archer analyses it in a few minutes, establishes your rights with their sources, and a jurist drafts the letter. Step by step.',
      canonical: `${SITE_URL}/how-it-works`,
      locale: 'en_US',
      alternates: frOnlyAlternates(`${SITE_URL}/how-it-works`),
    }),
    nl: generateMetadata({
      title: 'Hoe het werkt | scan, begrijp, handel',
      description:
        'Je scant je document, Archer analyseert het in enkele minuten, stelt je rechten vast met bronnen, en een jurist schrijft de brief. Stap voor stap.',
      canonical: `${SITE_URL}/how-it-works`,
      locale: 'nl_BE',
      alternates: frOnlyAlternates(`${SITE_URL}/how-it-works`),
    }),
  },

  // ── /blog (guides) ───────────────────────────────────────────────
  // Était « Legal Guides: Clear, Practical Advice », description sur
  // l'expulsion et la « severance negotiation » américaines. Le blog est
  // belge et francophone.
  blog: generateMetadata({
    title: 'Guides juridiques | le droit belge, simplement',
    description:
      'Amende, huissier, bail, facture, licenciement : des guides pas à pas, écrits par l’équipe juridique Archer pour les vraies situations, avec les sources.',
    canonical: `${SITE_URL}/blog`,
    locale: 'fr_BE',
    alternates: frOnlyAlternates(`${SITE_URL}/blog`),
  }),

  // ── /guides (le hub) ─────────────────────────────────────────────
  // La page qui relie tout : piliers, landings thématiques, articles.
  // Les landings n'apparaissaient dans aucun menu : orphelines, seule
  // porte le sitemap.
  guides: generateMetadata({
    title: 'Guides juridiques belges | par situation',
    description:
      'Tous les guides Archer par situation : amende et PV, huissier et dettes, bail et garantie locative, factures, abonnements, travail, assurance.',
    canonical: `${SITE_URL}/guides`,
    locale: 'fr_BE',
    alternates: frOnlyAlternates(`${SITE_URL}/guides`),
  }),

  // ── Pages de détresse, première vague (03/09/2026) ──────────────
  // NOINDEX TANT QUE ROMAIN N'A PAS SIGNÉ (porte humaine du workflow,
  // cf. content/pagesDetresse.js) : présentes au catalogue pour que la
  // page ait ses balises, absentes du sitemap et du pré-rendu, interdites
  // aux moteurs. SIGNÉES par Romain le 08/09/2026 (dix arbitrages, dernier
  // « ok ») : indexables, au sitemap, pré-rendues.
  'preavis-bail': generateMetadata({
    title: 'Préavis de bail en Belgique | tes droits de locataire',
    description:
      'Bail de 9 ans, courte durée, congé pour occupation personnelle : préavis, indemnité et date de départ à Bruxelles, en Wallonie et en Flandre, avec les sources.',
    canonical: `${SITE_URL}/preavis-bail`,
    locale: 'fr_BE',
    alternates: frOnlyAlternates(`${SITE_URL}/preavis-bail`),
    noindex: false,
  }),
  'aide-juridique-gratuite': generateMetadata({
    title: 'Avocat gratuit en Belgique | l’aide juridique (pro deo)',
    description:
      'Plafonds de revenus au 1er septembre 2026, personnes à charge, bureau d’aide juridique, recours : qui a droit à un avocat pro deo, et comment le demander.',
    canonical: `${SITE_URL}/aide-juridique-gratuite`,
    locale: 'fr_BE',
    alternates: frOnlyAlternates(`${SITE_URL}/aide-juridique-gratuite`),
    noindex: false,
  }),
  'recouvrement-frais-de-rappel': generateMetadata({
    title: 'Société de recouvrement, frais de rappel | ce qui est dû',
    description:
      'Intrum, Fiducre, EOS : premier rappel gratuit, 14 jours de délai, frais plafonnés, recouvreur jamais facturé. Le Livre XIX du Code de droit économique expliqué.',
    canonical: `${SITE_URL}/recouvrement-frais-de-rappel`,
    locale: 'fr_BE',
    alternates: frOnlyAlternates(`${SITE_URL}/recouvrement-frais-de-rappel`),
    noindex: false,
  }),

  // ── Pages avocat publiques ───────────────────────────────────────
  // Recrutement (/attorneys/join) et candidature (/attorney/apply). Elles
  // partaient nues chez les robots : coquille « AI Legal Assistant », sans
  // description ni canonical, alors que la première est au sitemap.
  // /attorneys/join est la création de compte SUR INVITATION (jeton dans
  // l'URL), pas une page de recrutement : sans jeton elle n'affiche qu'une
  // invitation invalide. NOINDEX, donc, et hors sitemap : un moteur n'a
  // rien à y lire. Elle garde ses balises pour le partage du lien
  // d'invitation. Le recrutement, c'est /attorneys et /attorney/apply.
  attorneysJoin: generateMetadata({
    title: 'Créer votre compte avocat | portail partenaires Archer',
    description:
      'Vous avez reçu une invitation Archer ? Créez votre compte avocat partenaire en quatre étapes : coordonnées, barreau et matières, mot de passe, confirmation.',
    canonical: `${SITE_URL}/attorneys/join`,
    locale: 'fr_BE',
    alternates: frOnlyAlternates(`${SITE_URL}/attorneys/join`),
    noindex: true,
  }),
  // /attorney/apply est écrite en anglais (formulaire historique) : ses
  // balises disent la langue de la page, pas celle du site.
  attorneyApply: generateMetadata({
    title: 'Apply as a partner attorney | the Archer form',
    description:
      'The application form for the Archer partner attorney network in Belgium: bar, practice areas, languages. The Archer team reviews every application.',
    canonical: `${SITE_URL}/attorney/apply`,
    locale: 'en_US',
    alternates: { en: `${SITE_URL}/attorney/apply` },
  }),
  // `evictionHelp`, `wrongfulTermination`, `severanceNegotiation` et
  // `aiLegalAssistant` ont été RETIRÉS le 17/08/2026 avec les quatre pages
  // piliers américaines qu'ils décrivaient (cf. `App.js`). Les entrées de
  // `sitemap.xml` sont parties avec eux : une route morte laissée au
  // manifeste, c'est une 404 soumise à Google.
};


/**
 * Pick the right metadata object for a page + language. Falls back to
 * FR (our primary market) when a lang isn't localised for the page, and
 * to English when neither exists. Returns null for unknown pages.
 */
export function getPageMetadata(pageKey, lang) {
  const entry = PAGE_METADATA[pageKey];
  if (!entry) return null;
  // Flat metadata (legacy US pillars) — return as-is regardless of lang.
  if (entry.title) return entry;
  // Nested lang map.
  const short = String(lang || 'fr').slice(0, 2).toLowerCase();
  return entry[short] || entry.fr || entry.en || Object.values(entry)[0];
}


/**
 * Les alternates hreflang d'une page : sa table, ou fr-BE seul par
 * défaut. PageHead et le générateur de manifeste passent tous deux par
 * ici : une seule règle, un seul endroit.
 */
export function alternatesFor(metadata) {
  return metadata?.alternates || frOnlyAlternates(metadata?.canonical || SITE_URL);
}

export {
  SITE_URL, SITE_NAME, DEFAULT_OG_IMAGE,
  SUPPORTED_LANGS, LOCALE_MAP, HREFLANG_MAP,
};
